import { endOfDay, parse, startOfDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useData as useVikeData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';

import { useDeepMemo } from '@/renderer/utils/hooks/useDeepMemo';
import useMe from '@/renderer/utils/useMe';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import type { ResultsFilters, ResultsFormFilters } from './FilterTypes';
import Filters from './Filters';
import { GradeRevisionContext, useGradeRevision } from './LiveStatsRefresh';
import { ResultsContents } from './ResultsContents';
import { ResultsHeader } from './ResultsHeader';
import { SPORT_IDS, type SportName } from './consts';
import { seededScope } from './resultsScope';
import { useResultsSummary } from './useResultsSummary';

const CFB_FILTERS: ResultsFilters = {
  sportId: '1',
  startDate: new Date('2021-07-01'),
  endDate: new Date('2025-01-22'),
  playType: 'full' as const,
};

const NFL_FILTERS: ResultsFilters = {
  sportId: '3',
  startDate: new Date('2021-07-01'),
  endDate: new Date('2025-02-09'),
  playType: 'full' as const,
};

const EMPTYOBJ = {};

const allTimeCountSchema: RequestSchema<API.MembershipProductStats> = {
  name: 'getAllTimeCountRequest',
  input: '/api/v1/messageReceipt/stats/me',
};

/**
 * Whether the signed-in member has any graded play on their account, all-time and across
 * every sport: `undefined` while unknown (or when not asked), else a boolean. One grouped
 * count, so it is cheap; only asked when /results has to pick its default scope.
 */
function useHasGradedPlays(enabled: boolean): boolean | undefined {
  const request = useRequest(
    allTimeCountSchema,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        suspendOnClient: false,
        clientOnly: true,
        pause: !enabled,
        query: { count: true },
      }),
      [enabled],
    ),
  );
  const d = request.data;
  if (!enabled) {
    return undefined;
  }
  if (d === undefined) {
    // A failed check must not hold the page: fall back to the route default.
    return request.error ? true : undefined;
  }
  return Number(d.win || 0) + Number(d.loss || 0) + Number(d.push || 0) > 0;
}

// The My/All scope the visitor is looking at, remembered per route (baseUrl) for the
// lifetime of the SPA session. Changing sport or season is a real navigation, which
// remounts this component; without this the scope would reset to the route's default
// every time and then flip back, rendering the other scope's data for a beat (#2004).
// Keyed by route so each route keeps its own default: a "Mine" picked on /results does
// not carry into an /archive link, which opens on All (#2080).
const rememberedScope: Record<string, 'me' | 'archive'> = {};

export function Results({
  userId: baseUserId,
  title: pageTitle = 'Results',
  baseUrl = '/results',
  breakdown = undefined,
  hideHeader = false,
}: {
  userId: API.Id | 'me' | 'archive';
  baseUrl?: string;
  title?: string;
  breakdown?: string;
  hideHeader?: boolean;
}) {
  const { urlParsed, routeParams } = usePageContext();
  const data = useVikeData() as
    | {
        seasons: API.Season[];
        seasonsSimple: {
          last5: API.Season[];
          first: API.Season;
          last: API.Season;
          selected: API.Season[];
        };
      }
    | undefined;

  const search = useDeepMemo(urlParsed.search || EMPTYOBJ);

  // The My/All toggle switches the data source live (client-only, no navigation): 'me'
  // reads the member's receipts, 'archive' the public release feed. The toggle only
  // appears for a signed-in user on the two public/member entry routes (#2004).
  const me = useMe();
  const canToggle = !!me?.role && (baseUserId === 'me' || baseUserId === 'archive');

  // Seed from the remembered scope so a sport/season navigation doesn't reset to the
  // route's default and then flip back — that reset+flip rendered a full set of the
  // other scope's data for a beat, which read as a page flash on every chip click.
  // `?scope=me` (the Release Center's pending-picks link) asks for My results
  // outright, ahead of any remembered or computed default (resultsScope.ts).
  const askedForMine = baseUserId === 'me' && search.scope === 'me';
  const remembered = seededScope({
    baseUserId,
    searchScope: search.scope,
    remembered: rememberedScope[baseUrl],
  });
  const [scopeState, setScopeState] = useState<API.Id | 'me' | 'archive'>(
    () => remembered || baseUserId,
  );

  const setScope = (next: 'me' | 'archive') => {
    rememberedScope[baseUrl] = next;
    setScopeState(next);
  };

  // Each route keeps its own default: /archive is the public, shareable record, so it
  // opens on All results for everyone (#2080); /results opens on My results. A member
  // with no graded plays at all would land on a 0-0 dead end there, so they get the RAS
  // record (All results) instead until they pick a scope themselves (#2142).
  const decideDefault = baseUserId === 'me' && !remembered;
  useEffect(() => {
    if (askedForMine) {
      rememberedScope[baseUrl] = 'me';
    }
  }, [askedForMine, baseUrl]);
  const hasGraded = useHasGradedPlays(decideDefault);
  // Derived in render, not flipped in an effect, so My never renders (or fetches) for a
  // commit before switching to All.
  const userId = decideDefault && hasGraded === false ? 'archive' : scopeState;
  useEffect(() => {
    if (decideDefault && hasGraded === false) {
      // Remember it so a sport/season navigation seeds straight into All, and sync the
      // state so later renders (where `remembered` is now set) stay on All. `userId` was
      // already All this render, so nothing on screen changes.
      rememberedScope[baseUrl] = 'archive';
      setScopeState('archive');
    }
  }, [decideDefault, hasGraded, baseUrl]);
  // Until that answer is in, hold the /results body rather than flash a 0-0 record.
  const choosingScope = decideDefault && hasGraded === undefined;

  // The page header names the scope in view, not the route it was opened on (#2142).
  // Only on the two toggle routes: the SEO landings (/nfl-results, /cfb-archive) keep
  // their own titles.
  // While the default scope is still being chosen it names neither one.
  const headerTitle =
    baseUrl === '/results' || baseUrl === '/archive'
      ? choosingScope
        ? 'Results'
        : userId === 'me'
          ? 'My Results'
          : 'All Results'
      : pageTitle;

  // How many pending plays this view has, reported up by PendingSection. `null` until it
  // settles, so nothing collapses on a guess.
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const sportId = routeParams.sportId
    ? SPORT_IDS[routeParams.sportId as SportName]
      ? `${SPORT_IDS[routeParams.sportId as SportName]}`
      : routeParams.sportId
    : SPORT_IDS[search.sportId as SportName]
      ? `${SPORT_IDS[search.sportId as SportName]}`
      : search.sportId || 'ras';

  const seasonId = routeParams.seasonId;

  const form = useForm<ResultsFormFilters>({
    defaultValues: {
      seasonId:
        routeParams.seasonId || (search.startDate || search.endDate ? undefined : 'current'),
      // convert 'nfl' in URL to '3' for form
      sportId,
      hidePropsFutures: !!search.playType && search.playType !== 'full',
      gameType: search.gameType || undefined,
    },
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const hidePropsFutures = form.watch('hidePropsFutures');

  const requestFilter = useMemo(() => {
    const res: ResultsFilters = {
      ...search,
      ...routeParams,
      sportId,
      // Supports a comma-separated list (e.g. ?gameType=fcs,extra) so a package
      // results link can scope to several play types at once.
      gameType: search.gameType
        ? (String(search.gameType).split(',').filter(Boolean) as API.GameTypes[])
        : undefined,
      startDate: search?.startDate
        ? startOfDay(parse(search.startDate, 'yyyyMMdd', new Date()))
        : undefined,
      endDate: search?.endDate
        ? endOfDay(parse(search.endDate, 'yyyyMMdd', new Date()))
        : undefined,
      playType:
        (search.playType as API.PlayTypes | 'full' | 'st' | 'pf' | 'hp') ||
        (hidePropsFutures ? ('hp' as const) : undefined),
    };

    if (!res.startDate && !res.endDate && seasonId !== 'all-time') {
      const endDate = data?.seasonsSimple?.selected?.[0]?.endDate;
      res.endDate = endDate ? new Date(endDate) : undefined;

      const startDate =
        data?.seasonsSimple?.selected?.[data?.seasonsSimple?.selected?.length - 1]?.startDate;
      res.startDate = startDate ? new Date(startDate) : undefined;
    }

    return res;
  }, [routeParams, search, data?.seasonsSimple, seasonId, hidePropsFutures, sportId]);

  const activeFilters =
    breakdown === 'cfb' ? CFB_FILTERS : breakdown === 'nfl' ? NFL_FILTERS : requestFilter;

  // "This view has nothing at all" — no graded plays AND no pending. It's a normal state
  // (a CFB member tapping CBB hits it every time), not an error, but it used to render a
  // 0-0 record over an empty chart, controls for a list that wasn't there and a legend
  // for nothing. Keep the honest record; drop the rest (#2004). Shares the summary
  // request with the header, so this costs no extra fetch.
  // A live grade/regrade refetches every stats request in this view (record, season
  // breakdown, chart); the play rows already update live (LiveStatsRefresh).
  const gradeRevision = useGradeRevision();
  const summary = useResultsSummary(userId, activeFilters, {
    paused: choosingScope,
    gradeRevision,
  });
  const hasNoPlays =
    !summary.isLoading &&
    summary.wins + summary.losses + summary.pushes === 0 &&
    pendingCount === 0;

  return (
    <GradeRevisionContext.Provider value={gradeRevision}>
      <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col overflow-auto">
        {/* Keep the app Header bar; the old sport/season nav block is hidden (`hide`) in
          favor of the redesigned header card below (#2004). */}
        <Filters
          baseUrl={baseUrl}
          form={form}
          hide
          hideHeader={hideHeader}
          title={headerTitle}
          sportId={routeParams.sportId || search.sportId}
          seasonId={seasonId}
        />
        <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
          {!breakdown && (
            <ResultsHeader
              baseUrl={baseUrl}
              userId={userId}
              filters={activeFilters}
              sportId={routeParams.sportId || (search.sportId as string) || undefined}
              seasonId={seasonId}
              seasons={data?.seasons}
              scope={userId === 'archive' ? 'archive' : 'me'}
              onScopeChange={setScope}
              canToggle={canToggle}
              hasNoPlays={hasNoPlays}
              startDate={typeof search.startDate === 'string' ? search.startDate : undefined}
              endDate={typeof search.endDate === 'string' ? search.endDate : undefined}
              loading={choosingScope}
            />
          )}
          {!choosingScope && (
            <ResultsContents
              baseUrl={baseUrl}
              form={form}
              userId={userId}
              filters={activeFilters}
              breakdown={breakdown}
              title={pageTitle}
              hasNoPlays={hasNoPlays}
              pendingCount={pendingCount}
              onPendingCount={setPendingCount}
            />
          )}
        </div>
      </div>
    </GradeRevisionContext.Provider>
  );
}
